import React from 'react';
import { createPortal } from 'react-dom';
import { formatDateTimeRange, getRecurrenceText } from '@features/appointments/utils/dateTimeFormatters';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Popper, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import type { ExpandedCardProps } from '../types';

import { ActionButtons } from './ActionButtons';
import {
  ActionFooter,
  ConfirmationRow,
  ConfirmedIcon,
  ContentColumn,
  ExpandedCardWrapper,
  Icon,
  MainContentRow,
  PatientName,
} from './ExpandedCard.styles';

/**
 * ExpandedCard - Hover expansion showing additional event details
 * Receives CSS variables via props (required since Popper renders to portal)
 */
export const ExpandedCard: React.FC<ExpandedCardProps> = ({
  event,
  isHovered,
  isModal,
  isTouch,
  anchorEl,
  icon,
  cssVars,
  variant,
  actionMessage,
  viewMode,
  onClose,
}) => {
  // Safety check: don't render if no anchor element
  if (!anchorEl) {
    return null;
  }

  // Extract event height for positioning calculation (safe after null check)
  const eventHeight = anchorEl.offsetHeight;

  // Determine if card should be open
  // Touch devices: modal only (no hover)
  // Desktop: hover or modal
  const isOpen = isTouch ? isModal : isHovered || isModal;

  // Backdrop element portalized to document.body using createPortal
  const backdropElement = isModal
    ? createPortal(
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        />,
        document.body,
      )
    : null;

  return (
    <>
      {backdropElement}

      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1000, pointerEvents: isModal ? 'auto' : 'none' }}
        modifiers={[
          {
            name: 'offset',
            options: {
              // 24px right (skidding) from top-left corner
              // Negative distance moves DOWN instead of up (eventHeight + 24px)
              offset: [24, -eventHeight + 24],
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
        ]}
      >
        <AnimatePresence>
          {isOpen && (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15, layout: { duration: 0.2 } }}
            >
              <ExpandedCardWrapper
                onClick={(e: React.MouseEvent) => {
                  // Prevent clicks within the modal from bubbling to calendar
                  e.stopPropagation();
                }}
                sx={{
                  ...cssVars,
                  // Larger sizing in modal mode
                  width: isModal ? '480px' : 'auto',
                  maxWidth: '90vw',
                }}
              >
                {/* Close button - only visible in modal mode */}
                {isModal && (
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      onClose();
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      padding: '4px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}

                <MainContentRow>
                  {icon && <Icon component={icon} />}
                  <ContentColumn>
                    {/* Patient name as title */}
                    <PatientName variant="h6">{event.title}</PatientName>

                    {/* Patient confirmation status */}
                    {event.resource.confirmation_status === 'confirmed' && (
                      <ConfirmationRow>
                        <ConfirmedIcon />
                        <Typography variant="body2">Patient Confirmed</Typography>
                      </ConfirmationRow>
                    )}

                    {/* Encounter type */}
                    {event.resource.encounter_type && (
                      <Typography variant="body2">{event.resource.encounter_type}</Typography>
                    )}

                    {/* Full date/time */}
                    <Typography variant="body2">
                      {formatDateTimeRange(event.start, event.end)}
                    </Typography>

                    {/* Recurrence info (if applicable) */}
                    {event.resource.series?.rrule && (
                      <Typography variant="body2">
                        {`Recurring ${getRecurrenceText(event.resource.series.rrule)}`}
                      </Typography>
                    )}

                    {/* Documents count */}
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                    {event.resource.encounter?.document_summary?.input_counts?.total && (
                      <Typography variant="body2">
                        Documents:{' '}
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                        {event.resource.encounter?.document_summary?.input_counts?.total}
                      </Typography>
                    )}
                  </ContentColumn>
                </MainContentRow>

                {/* Action buttons - only visible in modal mode for actionable variants */}
                {isModal && variant === 'actionable' && (
                  <ActionButtons
                    event={event}
                    onClose={onClose}
                    viewMode={viewMode}
                    isTouch={isTouch}
                  />
                )}

                {/* Action footer for actionable variant */}
                {variant === 'actionable' && actionMessage && (
                  <ActionFooter>{actionMessage}</ActionFooter>
                )}
              </ExpandedCardWrapper>
            </motion.div>
          )}
        </AnimatePresence>
      </Popper>
    </>
  );
};
